import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { RxHeroSlider } from '../components/rx/RxHeroSlider';
import { HealthManagementPanel } from '../components/rx/HealthManagementPanel';
import { DoctorConsultationBottomSheet } from '../components/rx/DoctorConsultationBottomSheet';
import { careActions, prescriptionActions, wellnessTools } from '../data/rxServices';
import { upcomingRxServices } from '../data/rxServicesUpcoming';
type LocationValue = {
  name: string;
} | null;
interface ServiceCardProps {
  title: string;
  description: string;
  path?: string;
  tone: string;
  icon: React.ElementType<{
    className?: string;
    strokeWidth?: number;
  }>;
  badge?: string;
  disabled?: boolean;
  onClick?: () => void;
}
function ServiceCard({
  title,
  description,
  path,
  tone,
  icon: Icon,
  badge,
  disabled = false,
  onClick
}: ServiceCardProps) {
  const navigate = useNavigate();
  return <button type="button" disabled={disabled} onClick={() => {
    if (disabled) return;
    if (onClick) onClick();else if (path) navigate(path);
  }} className={`group flex min-h-[128px] w-full flex-col rounded-xl border p-3 text-left shadow-[0_2px_10px_rgba(30,43,85,0.05)] transition-[border-color,transform] duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:ring-offset-2 sm:p-4 ${disabled ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60' : 'border-gray-100 bg-white hover:-translate-y-0.5 hover:border-[#cbd8eb]'}`} data-id="element-8098" data-mp="xmrb4k1h6wv6">
      <div className="flex w-full items-start justify-between gap-3" data-id="element-8099" data-mp="2iw3f6v9kfre1">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`} data-id="element-8100" data-mp="2ivag8ggum66g">
          <Icon className="h-5 w-5" strokeWidth={1.8} data-id="element-8101" data-mp="2dxiiv6xfpruv" />
        </span>
        {(badge || disabled) && <span className="rounded-md bg-[#eef4ff] px-2 py-1 text-[10px] font-bold tracking-wide text-[#315b96]" data-id="element-8102" data-mp="29tu8kghf4i5x">{disabled ? 'COMING SOON' : badge}</span>}
      </div>
      <div className="mt-3" data-id="element-8103" data-mp="1zxr2x0ncy1e8">
        <h3 className="text-sm font-bold leading-5 text-gray-900" data-id="element-8104" data-mp="1zysi9hbepa1r">{title}</h3>
        <p className="mt-1 hidden text-xs leading-5 text-gray-600 sm:block" data-id="element-8105" data-mp="1v697st80mv16">{description}</p>
      </div>
      <span className="mt-auto flex items-center gap-1 pt-2 text-xs font-bold text-[#1E2B55]" data-id="element-8106" data-mp="1q8fyvsfbvmx7">
        {disabled ? 'Available soon' : <>Get started <ChevronRightIcon className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-8107" data-mp="1m3q64cfyuogi" /></>}
      </span>
    </button>;
}
export function RxHomePage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationValue>(null);
  const [doctorConsultationOpen, setDoctorConsultationOpen] = useState(false);
  return <HelmetProvider data-id="element-8108" data-mp="1niifjee7jqx">
      <div className="min-h-screen bg-[#f7f8fb]" data-id="element-8109" data-mp="1oa6881r0d6n">
        <SEO title="RX Care | Prescriptions, pharmacists & health services" data-id="element-8110" data-mp="5p3ux3n0lw0l" />

        <div className="hidden md:block" data-id="element-8111" data-mp="5n178q9c4ez1">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement data-id="element-8112" data-mp="355a9mvkpfzo2" />
        </div>

        <main className="pb-24 pt-0 md:pb-0 md:pt-[var(--header-height-desktop)]" data-id="element-8113" data-mp="3ivnir19c1kez">
          <div className="md:hidden" data-id="element-8114" data-mp="2mdzhyc75niv4">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} transparentAtTop showExperienceSwitcher activeExperience="rx" data-id="element-8115" data-mp="2mcy98edm38gk" />
          </div>

          <RxHeroSlider data-id="element-8116" data-mp="22cepa9lpzin6" />

          <div className="mx-auto max-w-7xl space-y-8 px-4 py-7 md:px-6 md:py-12" data-id="element-8117" data-mp="22d7o8oeft3ur">
            <HealthManagementPanel data-id="element-8118" data-mp="2h1ugfqp5xq3e" />

            <section aria-labelledby="prescription-actions-heading" data-id="element-8119" data-mp="2h2vsnyhx0imp">
              <div className="flex items-end justify-between gap-4" data-id="element-8120" data-mp="2c9aqrvd7b4i8">
                <div data-id="element-8121" data-mp="2q0xzwvs8s7if">
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#7155aa]" data-id="element-8122" data-mp="2q0fxtiiwez2l">I have a prescription</p>
                  <h2 id="prescription-actions-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8123" data-mp="2l1urr2gytl68">Manage your prescription</h2>
                </div>
                <button type="button" onClick={() => navigate('/prescription/history')} className="hidden text-sm font-bold text-[#1E2B55] hover:underline sm:inline-flex" data-id="element-8124" data-mp="2v0anxjuunkml">View history</button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8125" data-mp="10nkraud3jxbi">
                {prescriptionActions.map(action => <ServiceCard key={action.title} {...action} data-id="element-8126" data-mp="3cb7fsohxpq0z" />)}
              </div>
            </section>

            <section aria-labelledby="care-actions-heading" data-id="element-8127" data-mp="3h8g1gz2akc6z">
              <div data-id="element-8128" data-mp="32kl7lfcjs1sb">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#287451]" data-id="element-8129" data-mp="32j1svt6jhysn">I need help</p>
                <h2 id="care-actions-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8130" data-mp="37hv5lje05lrr">Speak with a health professional</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8131" data-mp="2xkqjtko2jsbh">
                {careActions.map(action => <ServiceCard key={action.title} {...action} onClick={action.path === '/doctor-consultation' ? () => setDoctorConsultationOpen(true) : undefined} data-id="element-8132" data-mp="2jlh1aece4m4c" />)}
              </div>
            </section>

            <section aria-labelledby="upcoming-services-heading" data-id="element-8133" data-mp="htcetidnuh10">
              <div data-id="element-8134" data-mp="hs1anhgaz79d">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500" data-id="element-8135" data-mp="msoxdqxi11xs">Coming soon</p>
                <h2 id="upcoming-services-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8136" data-mp="lcb5cyd9zuts">More ways we will support your health</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8137" data-mp="gf1i9pdczv94">
                {upcomingRxServices.map(service => <ServiceCard key={service.title} {...service} path="" tone="bg-gray-100 text-gray-500" disabled data-id="element-8138" data-mp="6eisyf30zmjt" />)}
              </div>
            </section>

            <section aria-labelledby="wellness-tools-heading" data-id="element-8139" data-mp="141q5mql50fx4">
              <div data-id="element-8140" data-mp="z2n1r9gw8fqq">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#5d6482]" data-id="element-8141" data-mp="z47xh2ey24q6">Everyday wellness</p>
                <h2 id="wellness-tools-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8142" data-mp="uavi7iw5naa3">Useful health tools</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4" data-id="element-8143" data-mp="1mshd1mlkdw8v">
                {wellnessTools.map(tool => <ServiceCard key={tool.title} {...tool} data-id="element-8144" data-mp="1i2annkj8a7pk" />)}
              </div>
            </section>

            <section className="flex flex-col gap-4 rounded-xl border border-[#d6e4ef] bg-[#eff7fb] p-5 md:flex-row md:items-center md:justify-between md:p-6" data-id="element-8145" data-mp="1qayb8h1n1fz0">
              <div className="flex gap-3" data-id="element-8146" data-mp="1ucjssfp4aifj">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white text-[#256a88]" data-id="element-8147" data-mp="1zatpwvuejt3s">
                  <ShieldCheckIcon className="h-5 w-5" data-id="element-8148" data-mp="24b941pmid4mt" />
                </span>
                <div data-id="element-8149" data-mp="29ac9vcabbevu">
                  <h2 className="text-base font-bold text-gray-900" data-id="element-8150" data-mp="298rexlk35txg">Licensed care, handled securely</h2>
                  <p className="mt-1 text-sm leading-5 text-gray-600" data-id="element-8151" data-mp="2d9l1oblsl2or">Your prescription is reviewed by licensed professionals before it is processed.</p>
                </div>
              </div>
              <button type="button" onClick={() => navigate('/medicine-reminder')} className="h-10 flex-shrink-0 rounded-lg border border-[#a8c8d9] bg-white px-4 text-sm font-bold text-[#256a88] transition-colors duration-150 hover:bg-[#f8fdff]" data-id="element-8152" data-mp="2mzmh6f02xcs2">Set medicine reminders</button>
            </section>
          </div>
        </main>

        <div className="hidden md:block" data-id="element-8153" data-mp="3ic6enoyo06aw"><Footer data-id="element-8154" data-mp="3db8wvdhkhe9x" /></div>
        <MobileFooterNav data-id="element-8155" data-mp="3d9xlarhvr289" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={setSelectedLocation} data-id="element-8156" data-mp="3dapgi17kexy0" />
        <DoctorConsultationBottomSheet isOpen={doctorConsultationOpen} onClose={() => setDoctorConsultationOpen(false)} data-id="element-8157" data-mp="3mcqex1ejbq19" />
      </div>
    </HelmetProvider>;
}